import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faPaperclip,
  faPaperPlane,
  faPen,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import api from "../../api/api";
import dayjs from "dayjs";
import { MessageText } from "./FeedMessage";
import { deleteIcon, editIcon } from "../svg";
import { news, attach, sendmessage, micicon } from "./chatsvgs";
import { getPracticeId } from "../../config/practice";
import { openPatientFile, patientFileName, FOLDERS } from "../../api/patientFiles";
import PatientFileImg from "../../components/PatientFileImg";

const urlRegex = /(https?:\/\/[^\s]+)/g;

const NewsFeed = ({ setchat }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [editingMessageId, setEditingMessageId] = useState(null); // new
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);
  const practiseid = getPracticeId();
  const userConfig = useSelector((state) => state.userconfig);
  const MAX_ROWS = 4;
  const lineHeightRef = useRef(0);

  const autoResizeTextarea = () => {
    const ta = textareaRef.current;
    if (!ta) return;

    // Compute line-height once
    if (!lineHeightRef.current) {
      const cs = window.getComputedStyle(ta);
      lineHeightRef.current = parseFloat(cs.lineHeight || "24"); // fallback ~24px
    }

    // Reset then set to content height, but clamp to MAX_ROWS
    ta.style.height = "auto";
    const maxHeight = lineHeightRef.current * MAX_ROWS; // only content height; paddings are already in scrollHeight
    const newHeight = Math.min(ta.scrollHeight, maxHeight);
    ta.style.height = `${newHeight}px`;

    // Toggle scrolling past 4 rows
    ta.style.overflowY = ta.scrollHeight > maxHeight ? "auto" : "hidden";
  };

  useEffect(() => {
    autoResizeTextarea();
  }, [inputText]);

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    autoResizeTextarea();
  };

  const fetchData = async () => {
    try {
      const response = await api.get(`/v1/${practiseid}/newsfeed`);
      const fetchedMessages = response.data.map((item) => ({
        id: item.id,
        text: item.description || "",
        // the authenticated route URL first, then the stored name, then documents (the
        // old public URL, mapped to the same route). Never documents_link: that signed
        // link is for the patient apps (10-15). Was item.file, which the API never sent.
        documents: item.documents_url || item.documents_name || item.documents || item.file || "",
        documentsName: item.documents_name || "",
        createdOn: item.createdon,
        created_by: item.created_by || "",
        type: "received",
      }));
      fetchedMessages.sort(
        (a, b) => new Date(a.createdOn) - new Date(b.createdOn)
      );
      setMessages(fetchedMessages);
    } catch (error) {
      console.error("Fetch Error:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [practiseid]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleSend = async () => {
    if (!inputText && attachments.length === 0) return;

    // Check if we are editing
    if (editingMessageId) {
      // Call edit API
      try {
        await api.put(`/v1/${practiseid}/newsfeed/update`, null, {
          params: {
            rowid: editingMessageId,
            title: "",
            description: inputText,
            status: "A",
            updated_by: userConfig?.username || "Unknown",
          },
        });

        setMessages((prev) =>
          prev.map((m) =>
            m.id === editingMessageId ? { ...m, text: inputText } : m
          )
        );

        setInputText("");
        setAttachments([]);
        setEditingMessageId(null);
        fileInputRef.current.value = null;
      } catch (error) {
        console.error("Edit Error:", error);
        alert("Failed to update message.");
      }
      return;
    }

    // Normal send
    const formData = new FormData();
    formData.append("description", inputText || "");
    formData.append("created_by", userConfig?.username || "Unknown");
    formData.append("status", "A");
    formData.append("title", "");
    if (attachments.length > 0) {
      formData.append("file", attachments[0]);
    }

    try {
      const response = await api.post(`/v1/${practiseid}/newsfeed`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const newMessage = {
        id: response.data.id || Date.now(),
        text: inputText,
        documents:
          attachments.length > 0 ? URL.createObjectURL(attachments[0]) : "",
        type: "sent",
        createdOn: new Date().toISOString(),
        created_by: userConfig?.username || "You",
      };

      setMessages((prev) => [...prev, newMessage]);
      setInputText("");
      setAttachments([]);
      fileInputRef.current.value = null;
    } catch (error) {
      console.error("Send Error:", error);
      alert("Failed to send message.");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAttachments([file]);
    e.target.value = null;
  };

  const removeAttachment = () => setAttachments([]);

  // Edit message
  const handleEdit = (id) => {
    const msgToEdit = messages.find((m) => m.id === id);
    if (!msgToEdit) return;
    setInputText(msgToEdit.text);
    setAttachments([]);
    setEditingMessageId(id); // mark as editing
  };

  // Delete message
  const handleDelete = async (id) => {
    try {
      await api.delete(`/v1/${practiseid}/newsfeed/delete`, {
        params: { rowid: id, deleted_by: userConfig?.username || "Unknown" },
      });
      setMessages((prev) => prev.filter((m) => m.id !== id));
    } catch (error) {
      console.error("Delete Error:", error);
      alert("Failed to delete message.");
    }
  };

  // const renderMessageText = (text) => {
  //   const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;

  //   return text.split("\n").map((line, i) => {
  //     const parts = [];
  //     let lastIndex = 0;
  //     let match;

  //     // Use exec to safely find multiple links per line
  //     while ((match = urlRegex.exec(line)) !== null) {
  //       const url = match[0];
  //       const start = match.index;

  //       // Add plain text before the URL
  //       if (start > lastIndex) {
  //         parts.push(line.slice(lastIndex, start));
  //       }

  //       // Normalize link (add https:// if missing)
  //       const href = url.startsWith("http") ? url : `https://${url}`;

  //       // Add clickable link
  //       parts.push(
  //         <a
  //           key={`${i}-${start}`}
  //           href={href}
  //           target="_blank"
  //           rel="noopener noreferrer"
  //           className="text-blue-600 underline break-all"
  //         >
  //           {url}
  //         </a>
  //       );

  //       lastIndex = urlRegex.lastIndex;
  //     }

  //     // Add remaining text after last URL
  //     if (lastIndex < line.length) {
  //       parts.push(line.slice(lastIndex));
  //     }

  //     // Preserve line breaks
  //     return (
  //       <React.Fragment key={i}>
  //         {parts}
  //         {i < text.split("\n").length - 1 && <br />}
  //       </React.Fragment>
  //     );
  //   });
  // };

  return (
    <div className="flex flex-col h-screen w-[440px] border-l-2 border-[#FFEAD1] relative bg-white">
      

      <div className="fixed bg-white w-[440px] z-10 border-b border-gray-200">
        <div className="flex w-full items-center justify-between p-3">
          <div className="flex items-center gap-2">
            <div className="h-12 w-12 rounded-2xl bg-[#166FF6] flex items-center justify-center">
              {news}
            </div>
            <div>
              <div className="font-inter text-[14px] font-semibold">
                News Feed
              </div>
            </div>
          </div>
          <div
        className="fixed cursor-pointer text-gray-600 right-4 z-50"
        onClick={() => setchat(false)}
      >
        <FontAwesomeIcon icon={faXmark} size="lg" />
      </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto mt-[80px] mb-[120px] px-4 py-2 space-y-4">
        {messages.map((msg) => (
          <div key={msg.id} className=" mx-auto relative">
            <div className="text-sm font-normal font-inter text-center tracking-[-0.15px] text-[#6A7282] mb-1">
              {dayjs(msg.createdOn).format("DD MMM YYYY, hh:mm A")}
            </div>
            <div className="bg-[#F6F6F6] border-[0.53px] border-[#0000001A] text-[#1C1D22] rounded-[10px] px-3 pt-3 pb-1 break-words">
              {msg.text && <MessageText text={msg.text} />}
              {msg.documents && (
                <div className="mt-2">
                  {/\.(pdf|docx?)$/i.test(msg.documentsName || patientFileName(msg.documents)) ? (
                    <a
                      href={msg.documents}
                      onClick={(e) => {
                        e.preventDefault();
                        openPatientFile(msg.documents, FOLDERS.newsfeed).catch(() => {});
                      }}
                      className="text-blue-600 underline break-all"
                    >
                      {msg.documentsName || patientFileName(msg.documents)}
                    </a>
                  ) : (
                    <PatientFileImg
                      stored={msg.documents}
                      folder={FOLDERS.newsfeed}
                      alt="attachment"
                      className="max-w-[150px] rounded"
                    />
                  )}
                </div>
              )}
              <div className="text-xs flex flex-wrap justify-end text-black mt-4 mb-2 text-right">
                {/* {msg?.created_by} */}
                <div className="flex gap-2 ml-2">
                  <button
                    onClick={() => handleEdit(msg.id)}
                    // className="text-gray-900"
                  >
                    {/* <FontAwesomeIcon icon={faPen} size="sm" /> */}
                    {editIcon}
                  </button>
                  <button
                    onClick={() => handleDelete(msg.id)}
                    className="text-red-500"
                  >
                    {/* <FontAwesomeIcon icon={faTrash} size="sm" /> */}
                    {deleteIcon}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>

      <div className="fixed bottom-0 w-[440px] flex flex-col gap-2 p-3 border-t border-gray-200 bg-white">
        {attachments.length > 0 && (
          <div className="flex gap-2 items-center mb-2">
            <div className="relative">
              <img
                src={URL.createObjectURL(attachments[0])}
                alt={attachments[0].name}
                className="w-20 h-20 rounded object-cover"
              />
              <button
                onClick={removeAttachment}
                className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 text-xs"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          </div>
        )}

        <div
          style={{
            borderRadius: "14px",
            background: "#F7F9FB", // matches var(--color-grey-98)
            boxShadow:
              "0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px -1px rgba(0, 0, 0, 0.10)",
          }}
          className="flex items-center border w-full gap-3 px-2 border-[#D5D5D5]"
        >
          <textarea
            ref={textareaRef}
            rows={1}
            type="text"
            className="bg-transparent flex-grow py-1 pl-2 pr-3 text-base outline-none rounded-[14px] resize-none"
            placeholder="Type a message..."
            value={inputText}
            onChange={handleInputChange}
            onFocus={() => {
              // ensure correct height on focus too
              autoResizeTextarea();
            }}
            style={{
              maxHeight: lineHeightRef.current
                ? `${lineHeightRef.current * MAX_ROWS}px`
                : undefined,
              overflowY: "hidden",
            }}
          />

          <div className="h-auto mt-2">
            <div
              onClick={handleSend}
              style={{
                pointerEvents:
                  !inputText && attachments.length === 0 ? "none" : "auto",
                opacity:
                  !inputText && attachments.length === 0 ? 0.5 : 1,
                borderRadius: "4px",
                cursor:
                  !inputText && attachments.length === 0
                    ? "not-allowed"
                    : "pointer",
              }}
              className="inline-block"
            >
              {sendmessage}
            </div>
          </div>

          <input
            type="file"
            accept=".pdf,.png,.jpeg,.jpg,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp3,.wav,.mp4,.avi,.mov,.zip,.rar"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            id="fileInput"
          />

          <label htmlFor="fileInput">
            <div className="text-red-500 cursor-pointer mt-[11px]">
              {attach}
            </div>
          </label>
          <div className="mt-[3px]">
            <div
              onClick={() => {
                setInputText('');
              }}
              style={{
                cursor: "pointer",
              }}
            >
              {micicon}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsFeed;
